import React, { useRef, useState } from 'react';
import Map, { Source, Layer } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import sectorsGeoJson from '../geofiles/sectors.geojson';
import tmaFSSGeoJson from '../geofiles/tmaFSS.geojson';
import routesGeoJson from '../geofiles/routes.geojson';
import firsJson from '../firs.json';
import pointsGeoJson from '../geofiles/points.geojson';
import aerodromesGeoJson from '../geofiles/aerodromes.geojson';
import holdsGeoJson from '../geofiles/holds.geojson';

import { Sweden, Norway } from '../components/OtherAirspaces';
import Routes from '../components/Routes';
import Traffic from '../components/Traffic';
import FmpComponent from '../components/Fmp';

import { FaCrosshairs } from 'react-icons/fa';
import { sectorsOwnership, controllerColors } from '../utils/utils';

// Mapbox token
const mapboxToken =
  'pk.eyJ1Ijoib3R0b3R1aGt1bmVuIiwiYSI6ImNseG41dW9vaDAwNzQycXNleWI1MmowbHcifQ.1ZMRPeOQ7z9GRzKILnFNAQ';

// Helper to determine controller ownership
const getSectorOwner = (sectorCode, onlineControllers) => {
  const sectorControllers = sectorsOwnership[sectorCode];
  for (let controller of sectorControllers) {
    const callsign = `EFIN_${controller}_CTR`;
    if (onlineControllers.includes(callsign)) {
      return callsign;
    }
  }
  return null;
};

const Main = () => {
  const [onlineControllers] = useState([
    'EFIN_D_CTR',
    'EFIN_A_CTR',
    'EFIN_V_CTR',
    'EFIN_J_CTR',
  ]);
  const mapRef = useRef(null);

  const getSectorFillColor = (sectorCode) => {
    const owner = getSectorOwner(sectorCode, onlineControllers);
    return owner ? controllerColors[owner] : 'rgb(50, 50, 50)';
  };

  const handleMapLoad = (map) => {
    if (map.getLayer('airspace-fills-norway')) {
      map.moveLayer('airspace-fills-norway', 'sectors-fill');
      map.moveLayer('airspace-fills-sweden', 'sectors-fill');
      map.moveLayer('airspace-borders-norway', 'sectors-fill');
      map.moveLayer('airspace-borders-sweden', 'sectors-fill');
    }
  };

  const handleReset = () => {
    mapRef.current?.flyTo({
      center: [25, 65],
      zoom: 3.8,
      pitch: 0,
      bearing: 0,
      duration: 1000,
    });
  };

  return (
    <div
      style={{ height: '100vh', backgroundColor: '#1e1e1e', color: '#ffffff' }}
    >
      <Map
        ref={mapRef}
        initialViewState={{
          longitude: 25,
          latitude: 65,
          zoom: 3.8,
          pitch: 0,
          bearing: 0,
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/ottotuhkunen/cm3jvlpbz00ml01se7f5ya0bg"
        mapboxAccessToken={mapboxToken}
        attributionControl={false}
        pitchWithRotate={false}
        dragRotate={false}
        onLoad={(event) => handleMapLoad(event.target)}
      >
        {/* Sectors */}
        <Source type="geojson" data={sectorsGeoJson}>
          <Layer
            id="sectors-fill"
            type="fill"
            paint={{
              'fill-color': [
                'match',
                ['get', 'code'],
                'sector1',
                getSectorFillColor('sector1'),
                'sector2',
                getSectorFillColor('sector2'),
                'sector3',
                getSectorFillColor('sector3'),
                'sector4',
                getSectorFillColor('sector4'),
                'sector5',
                getSectorFillColor('sector5'),
                'sector6',
                getSectorFillColor('sector6'),
                'sector7',
                getSectorFillColor('sector7'),
                'sector8',
                getSectorFillColor('sector8'),
                'sector9',
                getSectorFillColor('sector9'),
                'sector10',
                getSectorFillColor('sector10'),
                'sector11',
                getSectorFillColor('sector11'),
                'sector12',
                getSectorFillColor('sector12'),
                'sector13',
                getSectorFillColor('sector13'),
                'sector14',
                getSectorFillColor('sector14'),
                'rgb(50, 50, 50)',
              ],
              'fill-opacity': 0.1,
            }}
            maxzoom={7}
          />
          <Layer
            id="sectors-border"
            type="line"
            paint={{
              'line-color': '#666666',
              'line-width': 0.3,
            }}
            filter={['all', ['<', ['zoom'], 6.5]]}
          />
        </Source>

        {/* Other airspaces */}
        <Sweden />
        <Norway />

        {/* TMA */}
        <Source type="geojson" data={tmaFSSGeoJson}>
          <Layer
            id="tma-lines"
            type="line"
            paint={{
              'line-color': ['concat', ['get', 'color']],
              'line-width': 1.5,
            }}
          />
          <Layer
            id="tma-labels"
            type="symbol"
            paint={{
              'text-color': '#ffffff',
              'text-halo-color': '#000000',
              'text-halo-width': 1,
            }}
          />
        </Source>

        {/* FIRs */}
        <Source type="geojson" data={firsJson}>
          <Layer
            id="firs-lines"
            type="line"
            paint={{
              'line-color': '#006400',
              'line-width': 1.5,
            }}
          />
        </Source>

        {/* Aerodromes */}
        <Source id="aerodromes" type="geojson" data={aerodromesGeoJson}>
          <Layer
            id="aerodromes-symbol"
            type="symbol"
            layout={{
              'text-field': '+',
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 13,
              'text-offset': [0, 0],
              'text-anchor': 'center',
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': '#8c8c8c',
              'text-halo-color': 'black',
              'text-halo-width': 0.9,
            }}
            minzoom={5}
          />
          <Layer
            id="aerodromes-label"
            type="symbol"
            layout={{
              'text-field': ['get', 'icao'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': 10,
              'text-offset': [0.8, 0],
              'text-anchor': 'left',
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': 'lightgray',
              'text-halo-color': '#000000',
              'text-halo-width': 1,
            }}
            minzoom={6}
          />
        </Source>

        {/* Routes */}
        <Source type="geojson" data={routesGeoJson}>
          <Layer
            id="route-lines"
            type="line"
            paint={{
              'line-color': ['concat', ['get', 'color']],
              'line-width': 0.8,
              'line-dasharray': [
                'case',
                ['==', ['get', 'type'], 'ad_connect'],
                [2, 3],
                [1],
              ],
            }}
          />
        </Source>

        {/* Holds */}
        <Source type="geojson" data={holdsGeoJson}>
          <Layer
            id="efro-holds-lines"
            type="line"
            paint={{
              'line-color': [
                'case',
                ['==', ['get', 'icao'], 'efro'],
                '#ff4000',
                ['==', ['get', 'icao'], 'entc'],
                '#00ff40',
                '#ff4000',
              ],
              'line-width': 0.7,
            }}
            minzoom={4}
          />
          <Layer
            id="efro-holds-text"
            type="symbol"
            layout={{
              'text-field': ['concat', ['get', 'info']],
              'text-size': 10,
              'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
              'icon-allow-overlap': true,
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': [
                'case',
                ['==', ['get', 'icao'], 'efro'],
                '#ff4000',
                ['==', ['get', 'icao'], 'entc'],
                '#00ff40',
                '#ff4000',
              ],
              'text-halo-color': 'black',
              'text-halo-width': 0.6,
            }}
            minzoom={6}
          />
        </Source>

        {/* Points */}
        <Source id="points" type="geojson" data={pointsGeoJson}>
          <Layer
            id="points-symbol"
            type="symbol"
            layout={{
              'icon-image': [
                'case',
                [
                  'all',
                  ['==', ['get', 'departure'], true],
                  ['==', ['get', 'arrival'], true],
                ],
                'fix-all',
                ['==', ['get', 'departure'], true],
                'fix-dep',
                ['==', ['get', 'arrival'], true],
                'fix-arr',
                'border-dot-13',
              ],
              'icon-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                4,
                0.7,
                5,
                0.9,
                5.01,
                1,
              ],
              'text-field': ['get', 'name'],
              'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
              'text-size': [
                'interpolate',
                ['linear'],
                ['zoom'],
                3.99,
                0,
                4,
                8,
                5,
                9,
                5.01,
                10,
              ],
              'text-offset': [0.6, 0.6],
              'text-anchor': 'left',
              'icon-allow-overlap': true,
              'text-allow-overlap': true,
            }}
            paint={{
              'text-color': [
                'match',
                ['get', 'type'],
                'hold',
                'yellow',
                'white',
              ],
              'text-halo-color': '#000000',
              'text-halo-width': 1,
            }}
          />
        </Source>

        {/* Other Components */}
        <FmpComponent mapboxToken={mapboxToken} />
        <Traffic />
        <Routes />
      </Map>

      {/* Reset button */}
      <button
        className="flex items-center justify-center fixed bottom-[170px] right-[12px] w-11 h-11 p-2 bg-sky-800 border border-sky-900 rounded-full shadow-lg hover:bg-sky-700 transition"
        onClick={handleReset}
      >
        <FaCrosshairs className="text-lg text-gray-300" />
      </button>
    </div>
  );
};

export default Main;
